<template>
  <v-container fluid class="small-wins-summary pa-4 pa-md-6">
    <!-- PR-UPGRADE-MODAL: per-tool premium-banner replaced by the one shared
         UpgradeToPlusModal (mounted globally in App.vue). Gating below is
         unchanged; it opens the shared modal via `upgradeModal/open`. -->
    <div class="mb-6">
      <h1 class="text-h4 font-weight-bold mb-1" style="color: #1E3A5F;">
        Period Summary
      </h1>
      <p class="text-body-1 text--secondary mb-0">
        Generate a summary of your accomplishments for any time period
      </p>
    </div>

    <v-card class="pa-5 mb-6" elevation="2">
      <div class="text-subtitle-1 font-weight-medium mb-3">
        Select Date Range
      </div>

      <div class="d-flex flex-wrap mb-4">
        <v-chip
          v-for="preset in presets"
          :key="preset.label"
          :color="activePreset === preset.label ? 'primary' : 'grey lighten-3'"
          :text-color="
            activePreset === preset.label ? 'white' : 'grey darken-2'
          "
          small
          class="mr-2 mb-2"
          @click="applyPreset(preset)"
        >
          {{ preset.label }}
        </v-chip>
      </div>

      <v-row>
        <v-col cols="12" sm="6">
          <v-menu
            ref="startMenu"
            v-model="startMenuOpen"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="startDate"
                label="Start Date"
                prepend-icon="mdi-calendar"
                readonly
                outlined
                dense
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="startDate"
              no-title
              @input="startMenuOpen = false"
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col cols="12" sm="6">
          <v-menu
            ref="endMenu"
            v-model="endMenuOpen"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="endDate"
                label="End Date"
                prepend-icon="mdi-calendar"
                readonly
                outlined
                dense
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="endDate"
              no-title
              @input="endMenuOpen = false"
            ></v-date-picker>
          </v-menu>
        </v-col>
      </v-row>

      <div v-if="startDate && endDate" class="text-body-2 text--secondary mb-3">
        <div class="d-flex flex-wrap align-center">
          <span v-if="filteredWins.length > 0" class="mr-4">
            <v-icon small class="mr-1">mdi-trophy</v-icon>
            {{ filteredWins.length }} win{{
              filteredWins.length !== 1 ? "s" : ""
            }}
          </span>
          <span v-if="filteredAccomplishments.length > 0" class="mr-4">
            <v-icon small class="mr-1">mdi-star-outline</v-icon>
            {{ filteredAccomplishments.length }} accomplishment{{
              filteredAccomplishments.length !== 1 ? "s" : ""
            }}
          </span>
          <span v-if="filteredEducation.length > 0" class="mr-4">
            <v-icon small class="mr-1">mdi-school-outline</v-icon>
            {{ filteredEducation.length }} education entr{{
              filteredEducation.length !== 1 ? "ies" : "y"
            }}
          </span>
          <span v-if="filteredRoleChanges.length > 0" class="mr-4">
            <v-icon small class="mr-1">mdi-briefcase-outline</v-icon>
            {{ filteredRoleChanges.length }} role{{
              filteredRoleChanges.length !== 1 ? "s" : ""
            }}
            active
          </span>
          <span v-if="totalFilteredItems === 0" class="text--disabled">
            No career evidence found in this period
          </span>
        </div>
      </div>
    </v-card>

    <v-card
      v-if="startDate && endDate && totalFilteredItems > 0"
      class="pa-5 mb-6"
      elevation="2"
    >
      <div class="text-subtitle-1 font-weight-medium mb-4">Summary Options</div>

      <v-row>
        <v-col cols="12" sm="6">
          <v-select
            v-model="summaryPurpose"
            :items="purposeOptions"
            label="Purpose"
            outlined
            dense
            prepend-icon="mdi-bullseye-arrow"
          ></v-select>
        </v-col>
        <v-col cols="12" sm="6">
          <v-select
            v-model="summaryFormat"
            :items="formatOptions"
            label="Format"
            outlined
            dense
            prepend-icon="mdi-format-list-text"
          ></v-select>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" sm="6">
          <v-select
            v-model="summaryTone"
            :items="toneOptions"
            label="Tone"
            outlined
            dense
            prepend-icon="mdi-message-text-outline"
          ></v-select>
        </v-col>
        <v-col cols="12" sm="6">
          <v-select
            v-model="summaryLength"
            :items="lengthOptions"
            label="Length"
            outlined
            dense
            prepend-icon="mdi-text-short"
          ></v-select>
        </v-col>
      </v-row>

      <v-btn
        color="primary"
        :disabled="!canGenerate"
        :loading="loading"
        @click="generateSummary"
      >
        <v-icon left>mdi-auto-fix</v-icon>
        Generate Summary
      </v-btn>
    </v-card>

    <div v-if="loading" class="text-center py-8">
      <v-progress-circular
        indeterminate
        color="primary"
        size="48"
      ></v-progress-circular>
      <p class="text-body-1 text--secondary mt-4">
        Generating your accomplishment summary...
      </p>
    </div>

    <div
      v-if="
        startDate &&
          endDate &&
          totalFilteredItems === 0 &&
          !loading &&
          !generatedSummary
      "
      class="text-center py-8"
    >
      <v-icon size="64" color="grey lighten-1"
        >mdi-calendar-blank-outline</v-icon
      >
      <p class="text-body-1 text--secondary mt-4">
        No career evidence found in this date range. Try a different period or
        capture some wins first!
      </p>
      <v-btn text color="primary" @click="$router.push('/SmallWinsCapture')">
        <v-icon left small>mdi-plus</v-icon>
        Capture a Win
      </v-btn>
    </div>

    <v-card
      v-if="generatedSummary && !loading"
      class="pa-5 mb-6"
      elevation="2"
      style="border-left: 4px solid #F59E0B;"
    >
      <div class="d-flex align-center justify-space-between mb-3">
        <div class="d-flex align-center">
          <v-icon color="amber darken-2" class="mr-2"
            >mdi-file-document-outline</v-icon
          >
          <h3 class="text-subtitle-1 font-weight-medium mb-0">
            Accomplishment Summary
          </h3>
        </div>
        <v-chip small outlined color="primary">
          {{ summaryItemCount }} item{{ summaryItemCount !== 1 ? "s" : "" }}
        </v-chip>
      </div>

      <div class="text-caption text--secondary mb-1">
        {{ formatDisplayDate(summaryStartDate) }} —
        {{ formatDisplayDate(summaryEndDate) }}
      </div>
      <div class="text-caption text--secondary mb-3">
        {{ summaryPurposeLabel }} · {{ summaryFormatLabel }} ·
        {{ summaryToneLabel }} · {{ summaryLengthLabel }}
      </div>

      <v-alert
        type="warning"
        text
        dense
        icon="mdi-alert-circle-outline"
        class="mb-3"
      >
        <strong>AI-generated content.</strong> Review every fact, name, number, and outcome against your wins before posting or sharing. AI can miss nuance or paraphrase incorrectly — your judgment is the final check.
      </v-alert>

      <div
        class="summary-content text-body-2 pa-4 mb-4"
        style="background: #f8f9fa; border-radius: 8px; line-height: 1.8; white-space: pre-wrap;"
      >
        {{ generatedSummary }}
      </div>

      <div class="d-flex flex-wrap">
        <v-btn
          small
          outlined
          color="primary"
          class="mr-2 mb-2"
          @click="copyToClipboard"
        >
          <v-icon left small>mdi-content-copy</v-icon>
          Copy to Clipboard
        </v-btn>
        <v-btn
          small
          outlined
          color="primary"
          class="mb-2"
          @click="downloadAsText"
        >
          <v-icon left small>mdi-download</v-icon>
          Download as Text
        </v-btn>
      </div>
    </v-card>

    <v-alert
      v-if="error"
      type="error"
      dismissible
      class="mb-4"
      @input="error = ''"
    >
      {{ error }}
    </v-alert>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarText }}
    </v-snackbar>

    <div v-if="winsLoading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <p class="text-body-2 text--secondary mt-4">Loading your wins...</p>
    </div>
  </v-container>
</template>

<script>
import { GetSmallWins } from "../../api/SmallWinsApi";
import { SagePeriodSummary } from "../../api/GuidedRantApi";
import { GetProfileContext } from "../../api/ProfileContextApi";
import * as resApi from "../../api/ResumeApi";
import { mapState } from "vuex";
import { mapFields } from "vuex-map-fields";

export default {
  name: "SmallWinsSummary",
  data: function() {
    return {
      startDate: "",
      endDate: "",
      startMenuOpen: false,
      endMenuOpen: false,
      allWins: [],
      allAccomplishments: [],
      allEducation: [],
      allRoles: [],
      generatedSummary: "",
      summaryStartDate: "",
      summaryEndDate: "",
      summaryWinCount: 0,
      summaryItemCount: 0,
      summaryPurposeLabel: "",
      summaryFormatLabel: "",
      summaryToneLabel: "",
      summaryLengthLabel: "",
      loading: false,
      winsLoading: false,
      error: "",
      activePreset: "",
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",
      profileContext: null,
      summaryPurpose: "Performance Review",
      summaryFormat: "Hybrid",
      summaryTone: "Professional",
      summaryLength: "Standard",
      purposeOptions: [
        "Performance Review",
        "Promotion Document",
        "Self-Assessment",
        "Manager Update",
        "LinkedIn Post",
      ],
      formatOptions: ["Bullet Points", "Narrative", "Hybrid"],
      toneOptions: ["Professional", "Conversational", "Executive Summary"],
      lengthOptions: ["Brief", "Standard", "Detailed"],
      presets: [
        { label: "Last 30 Days", days: 30 },
        { label: "Last Quarter", days: 90 },
        { label: "Last 6 Months", days: 180 },
        { label: "This Year", type: "thisYear" },
        { label: "Last Year", type: "lastYear" },
      ],
    };
  },
  computed: Object.assign(
    {},
    mapState(["resumeId"]),
    mapState("settings", ["companyViewEnabled"]),
    mapFields("auth", { isPremium: "isPremium" }),
    {
      filteredWins: function() {
        if (!this.startDate || !this.endDate) return [];
        var startMs = this.parseDateMs(this.startDate, false);
        var endMs = this.parseDateMs(this.endDate, true);
        var self = this;
        return this.allWins.filter(function(win) {
          var dateStr = win.createdAt || win.eventDate || "";
          if (!dateStr) return false;
          var winMs = self.parseDateToMs(dateStr);
          if (isNaN(winMs)) return false;
          return winMs >= startMs && winMs <= endMs;
        });
      },
      filteredAccomplishments: function() {
        if (!this.startDate || !this.endDate) return [];
        var startMs = this.parseDateMs(this.startDate, false);
        var endMs = this.parseDateMs(this.endDate, true);
        return this.allAccomplishments.filter(function(ach) {
          if (!ach.dateMs || isNaN(ach.dateMs)) return false;
          return ach.dateMs >= startMs && ach.dateMs <= endMs;
        });
      },
      filteredEducation: function() {
        if (!this.startDate || !this.endDate) return [];
        var startMs = this.parseDateMs(this.startDate, false);
        var endMs = this.parseDateMs(this.endDate, true);
        return this.allEducation.filter(function(edu) {
          if (!edu.dateMs || isNaN(edu.dateMs)) return false;
          return edu.dateMs >= startMs && edu.dateMs <= endMs;
        });
      },
      filteredRoleChanges: function() {
        if (!this.startDate || !this.endDate) return [];
        var startMs = this.parseDateMs(this.startDate, false);
        var endMs = this.parseDateMs(this.endDate, true);
        return this.allRoles.filter(function(role) {
          var roleStart = role.startMs || 0;
          var roleEnd = role.endMs || Date.now();
          return roleStart <= endMs && roleEnd >= startMs;
        });
      },
      totalFilteredItems: function() {
        return (
          this.filteredWins.length +
          this.filteredAccomplishments.length +
          this.filteredEducation.length +
          this.filteredRoleChanges.length
        );
      },
      canGenerate: function() {
        return (
          this.startDate &&
          this.endDate &&
          this.totalFilteredItems > 0 &&
          !this.loading
        );
      },
    },
  ),
  watch: {
    startDate: function() {
      this.generatedSummary = "";
    },
    endDate: function() {
      this.generatedSummary = "";
    },
  },
  mounted: function() {
    // PR-UPGRADE-MODAL: gating unchanged. AI period summaries are Plus, so a
    // free user gets the shared upgrade modal and is sent back to a surface
    // that works for them (history or CareerProfile fallback).
    if (!this.isPremium && !this.companyViewEnabled) {
      this.$store.dispatch("upgradeModal/open", {
        featureName: "AI period summaries",
        message:
          "AI period summaries are part of the AI tools. Plus turns them on, along with unlimited Sage coaching, whenever you want more."
      });
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        this.$router.replace({ path: "/CareerProfile" });
      }
      return;
    }
    this.loadAllWins();
    this.loadProfileContext();
    this.loadWorkAndEducation();
  },
  methods: {
    parseDateMs: function(dateStr, isEnd) {
      if (!dateStr) return NaN;
      var parts = dateStr.split("-");
      if (isEnd) {
        return new Date(
          parseInt(parts[0]),
          parseInt(parts[1]) - 1,
          parseInt(parts[2]),
          23,
          59,
          59,
          999,
        ).getTime();
      }
      return new Date(
        parseInt(parts[0]),
        parseInt(parts[1]) - 1,
        parseInt(parts[2]),
        0,
        0,
        0,
        0,
      ).getTime();
    },
    parseDateToMs: function(dateVal) {
      if (!dateVal) return NaN;
      if (typeof dateVal === "number") return dateVal;
      var str = String(dateVal);
      if (str.length === 10 && str.indexOf("-") === 4) {
        var p = str.split("-");
        return new Date(
          parseInt(p[0]),
          parseInt(p[1]) - 1,
          parseInt(p[2]),
          12,
          0,
          0,
          0,
        ).getTime();
      }
      return new Date(str).getTime();
    },
    loadAllWins: function() {
      var self = this;
      this.winsLoading = true;
      GetSmallWins()
        .then(function(result) {
          if (result.success) {
            var data = result.data;
            if (Array.isArray(data)) {
              self.allWins = data;
            } else if (data && Array.isArray(data.items)) {
              self.allWins = data.items;
            } else {
              self.allWins = [];
            }
          }
        })
        .catch(function(e) {
          console.error("Failed to load wins:", e);
          self.error = "Failed to load your wins. Please try again.";
        })
        .finally(function() {
          self.winsLoading = false;
        });
    },
    loadWorkAndEducation: function() {
      var self = this;
      if (!this.resumeId) return;
      resApi
        .GetFullResume(this.resumeId)
        .then(function(result) {
          if (result.success && result.data) {
            var data = result.data;
            var work = data.Work || data.work;
            var edu = data.Education || data.education;
            self.extractAccomplishmentsAndRoles(work);
            self.extractEducation(edu);
          }
        })
        .catch(function(e) {
          console.error("Failed to load work/education:", e);
        });
    },
    extractAccomplishmentsAndRoles: function(workData) {
      if (!workData) return;
      var workDict = workData.WorkDict || workData.workDict || workData;
      var accomplishments = [];
      var roles = [];
      var self = this;
      for (var key in workDict) {
        if (!workDict[key]) continue;
        var entry = workDict[key];
        var general = entry.workGeneral || entry.WorkGeneral || {};
        var roleInfo = entry.workRole || entry.WorkRole || {};
        var achievements = entry.achievementList || entry.AchievementList || [];
        var companyName =
          general.companyName || general.CompanyName || general.display || "";
        var positions = roleInfo.positions || roleInfo.Positions || [];
        var roleTitle =
          positions.length > 0
            ? positions[0].title || positions[0].Title || ""
            : "";
        var startDate = general.startDate || general.StartDate;
        var endDate = general.endDate || general.EndDate;
        var startMs = self.parseDateToMs(startDate);
        var endMs = self.parseDateToMs(endDate);
        if (!isNaN(startMs)) {
          roles.push({
            title: roleTitle,
            company: companyName,
            startMs: startMs,
            endMs: isNaN(endMs) ? Date.now() : endMs,
            isCurrent:
              general.workingCurrently || general.WorkingCurrently || false,
            key: key,
          });
        }
        achievements.forEach(function(ach) {
          var achDate = startMs;
          if (!isNaN(endMs)) {
            achDate = endMs;
          }
          accomplishments.push({
            text:
              ach.displayCustom ||
              ach.displayAuto ||
              ach.DisplayCustom ||
              ach.DisplayAuto ||
              "",
            background: ach.background || ach.Background || "",
            responsibility: ach.responsibility || ach.Responsibility || "",
            actionResult: ach.actionResult || ach.ActionResult || "",
            measurement: ach.measurement || ach.Measurement || "",
            company: companyName,
            role: roleTitle,
            dateMs: achDate,
            source: "experience",
          });
        });
      }
      this.allAccomplishments = accomplishments;
      this.allRoles = roles;
    },
    extractEducation: function(eduData) {
      if (!eduData) return;
      var eduDict = eduData.EducationDict || eduData.educationDict || eduData;
      var education = [];
      var self = this;
      for (var key in eduDict) {
        if (!eduDict[key]) continue;
        var entry = eduDict[key];
        var general = entry.educationGeneral || entry.EducationGeneral || {};
        var degree = entry.educationDegree || entry.EducationDegree || {};
        var endDate =
          general.dateEnd ||
          general.DateEnd ||
          general.dateExpected ||
          general.DateExpected;
        var dateMs = self.parseDateToMs(endDate);
        if (isNaN(dateMs)) {
          var startDate = general.dateStart || general.DateStart;
          dateMs = self.parseDateToMs(startDate);
        }
        var schoolName =
          general.universityName ||
          general.UniversityName ||
          general.schoolName ||
          general.SchoolName ||
          "";
        var degreeName = degree.degreeName || degree.DegreeName || "";
        var degreeArea = degree.degreeArea || degree.DegreeArea || "";
        education.push({
          school: schoolName,
          degree: degreeName,
          area: degreeArea,
          dateMs: dateMs,
          graduated: general.graduated || general.Graduated || false,
          source: "education",
        });
      }
      this.allEducation = education;
    },
    loadProfileContext: function() {
      var self = this;
      if (!this.resumeId) return;
      GetProfileContext(this.resumeId)
        .then(function(result) {
          if (result.success && result.data) {
            self.profileContext = result.data;
          }
        })
        .catch(function() {});
    },
    applyPreset: function(preset) {
      var now = new Date();
      var start;
      var end;
      this.activePreset = preset.label;

      if (preset.type === "thisYear") {
        start = new Date(now.getFullYear(), 0, 1);
        end = now;
      } else if (preset.type === "lastYear") {
        start = new Date(now.getFullYear() - 1, 0, 1);
        end = new Date(now.getFullYear() - 1, 11, 31);
      } else {
        end = now;
        start = new Date(now.getTime() - preset.days * 24 * 60 * 60 * 1000);
      }

      this.startDate = this.toISODate(start);
      this.endDate = this.toISODate(end);
    },
    toISODate: function(date) {
      var year = date.getFullYear();
      var month = String(date.getMonth() + 1).padStart(2, "0");
      var day = String(date.getDate()).padStart(2, "0");
      return year + "-" + month + "-" + day;
    },
    buildWinDetails: function(wins) {
      return wins.map(function(win) {
        var detail = {
          rawInput: win.rawInput || win.description || "",
          parsedType: win.parsedType || "note",
          createdAt: win.createdAt || "",
          eventDate: win.eventDate || "",
        };
        if (win.aiDraft) {
          detail.situation = win.aiDraft.situation || "";
          detail.task = win.aiDraft.task || "";
          detail.action = win.aiDraft.action || "";
          detail.result = win.aiDraft.result || "";
          detail.summary = win.aiDraft.summary || "";
          detail.category = win.aiDraft.category || "";
        }
        if (win.linkedTo) {
          detail.linkedJob = win.linkedTo;
        }
        if (win.status) {
          detail.status = win.status;
        }
        return detail;
      });
    },
    buildProfileSummary: function() {
      if (!this.profileContext) return null;
      var ctx = this.profileContext;
      var summary = {};
      if (ctx.name) {
        summary.name = [ctx.name.firstName, ctx.name.lastName]
          .filter(Boolean)
          .join(" ");
      }
      if (ctx.workExperience && ctx.workExperience.length > 0) {
        var current = ctx.workExperience[0];
        summary.currentRole =
          current.positions && current.positions[0]
            ? current.positions[0].title
            : "";
        summary.currentCompany = current.company || "";
      }
      if (ctx.skills) {
        var hardSkills = (ctx.skills.hard || []).map(function(s) {
          return s.text || s;
        });
        var softSkills = (ctx.skills.soft || []).map(function(s) {
          return s.text || s;
        });
        summary.topSkills = hardSkills.slice(0, 10);
        summary.softSkills = softSkills.slice(0, 5);
      }
      summary.totalWins = (ctx.smallWins || []).length;
      summary.totalAchievements = (ctx.workExperience || []).reduce(function(
        acc,
        w,
      ) {
        return acc + (w.achievements || []).length;
      },
      0);
      return summary;
    },
    buildAccomplishmentDetails: function(accomplishments) {
      return accomplishments.map(function(ach) {
        return {
          text: ach.text || "",
          background: ach.background || "",
          responsibility: ach.responsibility || "",
          actionResult: ach.actionResult || "",
          measurement: ach.measurement || "",
          company: ach.company || "",
          role: ach.role || "",
          source: "experience",
        };
      });
    },
    buildEducationDetails: function(education) {
      return education.map(function(edu) {
        return {
          school: edu.school || "",
          degree: edu.degree || "",
          area: edu.area || "",
          graduated: edu.graduated || false,
          source: "education",
        };
      });
    },
    buildRoleDetails: function(roles) {
      return roles.map(function(role) {
        return {
          title: role.title || "",
          company: role.company || "",
          isCurrent: role.isCurrent || false,
          source: "role",
        };
      });
    },
    generateSummary: function() {
      var self = this;
      this.loading = true;
      this.error = "";
      this.generatedSummary = "";

      var winDetails = this.buildWinDetails(this.filteredWins);
      var accomplishmentDetails = this.buildAccomplishmentDetails(
        this.filteredAccomplishments,
      );
      var educationDetails = this.buildEducationDetails(this.filteredEducation);
      var roleDetails = this.buildRoleDetails(this.filteredRoleChanges);

      var allItems = [].concat(
        winDetails.map(function(w) {
          w.source = "win";
          return w;
        }),
        accomplishmentDetails,
        educationDetails,
        roleDetails,
      );

      var profileSummary = this.buildProfileSummary();
      var options = {
        format: this.summaryFormat,
        purpose: this.summaryPurpose,
        tone: this.summaryTone,
        length: this.summaryLength,
      };

      this.summaryStartDate = this.startDate;
      this.summaryEndDate = this.endDate;
      this.summaryWinCount = this.filteredWins.length;
      this.summaryItemCount = this.totalFilteredItems;
      this.summaryPurposeLabel = this.summaryPurpose;
      this.summaryFormatLabel = this.summaryFormat;
      this.summaryToneLabel = this.summaryTone;
      this.summaryLengthLabel = this.summaryLength;

      SagePeriodSummary(
        allItems,
        this.startDate,
        this.endDate,
        options,
        profileSummary,
      )
        .then(function(result) {
          if (result.success && result.data) {
            self.generatedSummary = result.data.summary || result.data;
          } else {
            self.error =
              result.msg || "Failed to generate summary. Please try again.";
          }
        })
        .catch(function(e) {
          console.error("Summary generation error:", e);
          self.error =
            "An error occurred while generating the summary. Please try again.";
        })
        .finally(function() {
          self.loading = false;
        });
    },
    copyToClipboard: function() {
      var self = this;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(this.generatedSummary)
          .then(function() {
            self.showSnackbar("Copied to clipboard!", "success");
          })
          .catch(function() {
            self.fallbackCopy();
          });
      } else {
        this.fallbackCopy();
      }
    },
    fallbackCopy: function() {
      var textarea = document.createElement("textarea");
      textarea.value = this.generatedSummary;
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand("copy");
        this.showSnackbar("Copied to clipboard!", "success");
      } catch (e) {
        this.showSnackbar("Failed to copy", "error");
      }
      document.body.removeChild(textarea);
    },
    downloadAsText: function() {
      var header =
        "Period Summary: " +
        this.formatDisplayDate(this.summaryStartDate) +
        " - " +
        this.formatDisplayDate(this.summaryEndDate) +
        "\n";
      header +=
        "Purpose: " +
        this.summaryPurposeLabel +
        " | Format: " +
        this.summaryFormatLabel +
        " | Tone: " +
        this.summaryToneLabel +
        " | Length: " +
        this.summaryLengthLabel +
        "\n";
      header += "Items included: " + this.summaryItemCount + "\n";
      header += "Generated: " + new Date().toLocaleDateString() + "\n";
      header += "\u2500".repeat(50) + "\n\n";
      var content = header + this.generatedSummary;
      var blob = new Blob([content], { type: "text/plain" });
      var url = URL.createObjectURL(blob);
      var a = document.createElement("a");
      a.href = url;
      a.download =
        "period-summary-" +
        this.summaryStartDate +
        "-to-" +
        this.summaryEndDate +
        ".txt";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      this.showSnackbar("Downloaded!", "success");
    },
    formatDisplayDate: function(dateStr) {
      if (!dateStr) return "";
      var parts = dateStr.split("-");
      var date = new Date(
        parseInt(parts[0]),
        parseInt(parts[1]) - 1,
        parseInt(parts[2]),
      );
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    },
    showSnackbar: function(text, color) {
      this.snackbarText = text;
      this.snackbarColor = color || "success";
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.small-wins-summary {
  margin: 0 auto;
}

.summary-content {
  font-family: inherit;
}

.theme--dark .summary-content {
  background: #2d2d2d !important;
  color: #e0e0e0;
}
</style>
